import { formatCurrency } from '../../../utils/formatCurrency';

/**
 * Business insights generation
 * Generates intelligent insights based on business data
 */

/**
 * Generate overdue insights
 */
export function generateOverdueInsights(invoices) {
  const now = new Date();
  const overdueInvoices = invoices.filter(inv => 
    (inv.status === 'sent' || inv.status === 'partially_paid') && 
    new Date(inv.due_date) < now && 
    inv.balance_due > 0
  );
  
  if (overdueInvoices.length === 0) return null;
  
  const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + inv.balance_due, 0);
  const criticalOverdue = overdueInvoices.filter(inv => {
    const daysOverdue = Math.floor((now - new Date(inv.due_date)) / (1000 * 60 * 60 * 24));
    return daysOverdue > 15;
  });
  
  if (criticalOverdue.length > 0) {
    return {
      type: 'alert',
      title: `${formatCurrency(totalOverdue)} at risk from overdue invoices`,
      description: `${criticalOverdue.length} ${criticalOverdue.length === 1 ? 'invoice' : 'invoices'} overdue by 15+ days`,
      action: 'Follow Up',
    };
  }
  
  return {
    type: 'warning',
    title: `${formatCurrency(totalOverdue)} pending from overdue invoices`,
    description: `${overdueInvoices.length} ${overdueInvoices.length === 1 ? 'invoice needs' : 'invoices need'} attention`,
    action: 'View Details',
  };
}

/**
 * Generate collection performance insights
 */
export function generateCollectionInsights(invoices) {
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  
  const thisMonthPaid = invoices.filter(inv => {
    const paidDate = new Date(inv.updated_at);
    return inv.status === 'paid' && paidDate >= thisMonthStart;
  });
  
  const lastMonthPaid = invoices.filter(inv => {
    const paidDate = new Date(inv.updated_at);
    return inv.status === 'paid' && paidDate >= lastMonthStart && paidDate <= lastMonthEnd;
  });
  
  if (thisMonthPaid.length === 0 || lastMonthPaid.length === 0) return null;
  
  const improvement = ((thisMonthPaid.length - lastMonthPaid.length) / lastMonthPaid.length) * 100;
  
  if (improvement > 10) {
    return {
      type: 'success',
      title: `Collections improved ${Math.round(improvement)}% this month`,
      description: `${thisMonthPaid.length} payments received vs ${lastMonthPaid.length} last month`,
    };
  } else if (improvement < -10) {
    return {
      type: 'warning',
      title: `Collections decreased ${Math.abs(Math.round(improvement))}% this month`,
      description: `${thisMonthPaid.length} payments received vs ${lastMonthPaid.length} last month`,
    };
  }
  
  return null;
}

/**
 * Generate draft invoice insights
 */
export function generateDraftInsights(invoices) {
  const drafts = invoices.filter(inv => inv.status === 'draft');
  
  if (drafts.length === 0) return null;
  
  const totalDraftValue = drafts.reduce((sum, inv) => sum + inv.total, 0);
  
  return {
    type: 'info',
    title: `Draft invoices worth ${formatCurrency(totalDraftValue)} awaiting approval`,
    description: `${drafts.length} ${drafts.length === 1 ? 'draft is' : 'drafts are'} ready to send`,
    action: 'Review Drafts',
  };
}

/**
 * Generate client concentration insights
 */
export function generateClientConcentrationInsights(invoices) {
  const pendingInvoices = invoices.filter(inv => 
    (inv.status === 'sent' || inv.status === 'partially_paid') && 
    inv.balance_due > 0
  );
  
  if (pendingInvoices.length < 3) return null;
  
  // Group by client
  const clientTotals = {};
  pendingInvoices.forEach(inv => {
    if (!clientTotals[inv.client_name]) {
      clientTotals[inv.client_name] = 0;
    }
    clientTotals[inv.client_name] += inv.balance_due;
  });
  
  // Sort by amount
  const sortedClients = Object.entries(clientTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2);
  
  const topTwoTotal = sortedClients.reduce((sum, [, amount]) => sum + amount, 0);
  const totalPending = pendingInvoices.reduce((sum, inv) => sum + inv.balance_due, 0);
  const concentration = (topTwoTotal / totalPending) * 100;
  
  if (concentration > 50) {
    return {
      type: 'info',
      title: `Top 2 clients account for ${Math.round(concentration)}% of pending collections`,
      description: `${formatCurrency(topTwoTotal)} concentrated in ${sortedClients.length} clients`,
    };
  }
  
  return null;
}

/**
 * Generate payment cycle insights
 */
export function generatePaymentCycleInsights(invoices) {
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  
  const calculateAvgDays = (invoiceList) => {
    if (invoiceList.length === 0) return 0;
    const totalDays = invoiceList.reduce((sum, inv) => {
      const sentDate = new Date(inv.created_at);
      const paidDate = new Date(inv.updated_at);
      return sum + Math.floor((paidDate - sentDate) / (1000 * 60 * 60 * 24));
    }, 0);
    return Math.round(totalDays / invoiceList.length);
  };
  
  const thisMonthPaid = invoices.filter(inv => {
    const paidDate = new Date(inv.updated_at);
    return inv.status === 'paid' && paidDate >= thisMonthStart;
  });
  
  const lastMonthPaid = invoices.filter(inv => {
    const paidDate = new Date(inv.updated_at);
    return inv.status === 'paid' && paidDate >= lastMonthStart && paidDate <= lastMonthEnd;
  });
  
  if (thisMonthPaid.length === 0 || lastMonthPaid.length === 0) return null;
  
  const thisMonthAvg = calculateAvgDays(thisMonthPaid);
  const lastMonthAvg = calculateAvgDays(lastMonthPaid);
  const improvement = lastMonthAvg - thisMonthAvg;
  
  if (improvement > 2) {
    return {
      type: 'success',
      title: `Payment cycle improved by ${improvement} days`,
      description: `Average payment time: ${thisMonthAvg} days (was ${lastMonthAvg} days)`,
    };
  } else if (improvement < -2) {
    return {
      type: 'warning',
      title: `Payment cycle increased by ${Math.abs(improvement)} days`,
      description: `Average payment time: ${thisMonthAvg} days (was ${lastMonthAvg} days)`,
    };
  }
  
  return null;
}

/**
 * Generate all business insights
 */
export function generateAllInsights(invoices) {
  const insights = [
    generateOverdueInsights(invoices),
    generateCollectionInsights(invoices),
    generateDraftInsights(invoices),
    generateClientConcentrationInsights(invoices),
    generatePaymentCycleInsights(invoices),
  ].filter(Boolean);
  
  // Prioritize by type: alert > warning > success > info
  const priority = { alert: 0, warning: 1, success: 2, info: 3 };
  insights.sort((a, b) => priority[a.type] - priority[b.type]);
  
  return insights;
}
